import React, { useEffect, useState, useCallback } from "react";
import { ModalBody } from "reactstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import CloudStorage from "../../../../CloudStorage/CloudStorage";
import { updateProfile } from "../../../../redux/profile/action";
import { useDispatch, useSelector } from "react-redux";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { GetCountry } from "../../../../redux/master/action";
import ReactSelect from "react-select";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import { urtoast } from "../../../Layouts/CustomPopup/eventEmitter";
import { namevalidation, titlevalidation } from "../../../Common/commonvalidation";
import { getCroppedImg } from "../../../../utils/cropUtils";
import ProfileImageWithFallback from "../../../../common/ProfileImageWithFallback";
import { validImageTypes } from "../../../../assests/extention/fileextention";
import { handleNameKeyDown } from "../../../Common/FewCommonMethods";
import useFileValidator from "../../../../hooks/useFileValidator";
import UploadLoader from "../../../Common/Loader/UploadLoader";
import { decodeHtml } from "../../../../assests/extention/commonfunction";

export default function ProfileModelProfile({
  edittype,
  formData,
  setFormData,
  setModelProfile,
}) {
  const profile = useSelector(({ profile: { profiledata } }) => profiledata);
  const dispatch = useDispatch();

  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(
    formData.profile_image || profile.profile_image
  );
  const [selectCountry, setSelectCountry] = useState("");

  // Cropper states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  const initialValues = {
    first_name: decodeHtml(formData.first_name || profile.first_name || ""),
    last_name: decodeHtml(formData.last_name || profile.last_name || ""),
    country_id: formData.country_id || profile.country_id || "",
  };

  const validationSchema = Yup.object().shape({
    first_name: namevalidation,
    last_name: titlevalidation,
    country_id: Yup.string()
      .trim()
      .required("This Field is Required")
      .test(
        "not-only-spaces",
        "Please enter a valid value",
        (value) => value.trim() !== ""
      ),
  });

  const handleSubmit = async (values) => {
    let profile_image = imageUrl;

    if (imageFile) {
      const storage = getStorage();
      const storageRef = ref(storage, `profile_images/${imageFile.name}`);
      const uploadTask = uploadBytesResumable(storageRef, imageFile);

      uploadTask.on(
        "state_changed",
        () => { },
        (error) => {
          console.error("Error uploading image: ", error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            profile_image = downloadURL;
            submitData(values, profile_image);
          });
        }
      );
    } else {
      submitData(values, profile_image);
    }
  };

  const submitData = (values, profile_image) => {
    const data = {
      type: "profile",
      first_name: values.first_name,
      last_name: values.last_name,
      profile_image,
      country_id: values.country_id,
    };

    if (edittype === 1) {
      dispatch(updateProfile(data));
      setModelProfile((state) => !state);
    } else {
      setFormData({
        ...formData,
        first_name: values.first_name,
        last_name: values.last_name,
        profile_image,
        country_id: values.country_id,
        country_title: selectCountry.label,
      });
      setModelProfile((state) => !state);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const showCroppedImage = async () => {
    try {
      const croppedFile = await getCroppedImg(imageUrl, croppedAreaPixels);
      setImageFile(croppedFile);
      setImageUrl(URL.createObjectURL(croppedFile));
      setShowCropper(false);
    } catch (err) {
      console.error(err);
    }
  };


  const { validating, validateFiles } = useFileValidator();

  const { getRootProps, getInputProps } = useDropzone({
    accept: validImageTypes.join(", "),
    maxSize: 10 * 1024 * 1024, // 10 MB
    onDrop: async (acceptedFiles, fileRejections) => {
      // 🧠 Handle rejected files first
      if (fileRejections.length > 0) {
        fileRejections.forEach((rejection) => {
          const { file, errors } = rejection;
          errors.forEach((err) => {
            if (err.code == "file-too-large") {
              urtoast(`File size exceeds 10MB limit`, 2);
            } else if (err.code == "file-invalid-type") {
              urtoast(`"${file.name}" is not a valid image (JPG, JPEG, PNG).`, 2);
            } else {
              urtoast(`"${file.name}" could not be uploaded.`, 2);
            }
          });
        });
        return;
      }

      // 🛡️ Validate all files via backend API
      const isValid = await validateFiles(acceptedFiles);
      if (!isValid) return;

      // 🧾 Handle accepted file
      const file = acceptedFiles[0];
      if (!file) return;

      // 🔍 Validate file type manually as well (extra safety)
      if (!validImageTypes.includes(file.type)) {
        urtoast("Please upload a valid image (JPG, JPEG, PNG).", 2);
        return;
      }

      // ✅ All good — proceed
      setImageFile(file);
      setImageUrl(URL.createObjectURL(file));
      setShowCropper(true);
    },
  });

  const countrylist = useSelector(({ master: { countrylist } }) => countrylist);
  const options = countrylist?.find(
    (item) => item.id == initialValues.country_id
  );
  const country_index = { value: options?.id, label: options?.title };

  useEffect(() => {
    dispatch(GetCountry());
  }, [dispatch]);

  useEffect(() => {
    if (initialValues.country_id) {
      setSelectCountry(country_index);
    }
  }, [initialValues.country_id]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ setFieldValue }) => (
        <>
          <button
            onClick={() => setModelProfile((state) => !state)}
            className="btn close-button"
          >
            <img
              src={`${CloudStorage.cloudurl}/Assets/Images/common/close-icon.svg`}
              alt="Close"
            />
          </button>
          <Form>
            <ModalBody>
              <h4>Edit Profile</h4>
              <div className="form-group">
                <label className="label-s">First Name</label>
                <Field
                  className="form-control input"
                  type="text"
                  name="first_name"
                  placeholder="Your First Name"
                  onKeyDown={handleNameKeyDown}

                />
                <ErrorMessage
                  name="first_name"
                  component="div"
                  className="error"
                />
              </div>
              <div className="form-group">
                <label className="label-s">Last Name</label>
                <Field
                  className="form-control input"
                  type="text"
                  name="last_name"
                  placeholder="Your Last Name"
                  onKeyDown={handleNameKeyDown}
                />
                <ErrorMessage
                  name="last_name"
                  component="div"
                  className="error"
                />
              </div>
              <div className="form-group">
                <label className="label-s">Country</label>
                <ReactSelect
                  options={
                    Array.isArray(countrylist) &&
                    countrylist.map((country) => ({
                      value: country.id,
                      label: country.title,
                    }))
                  }
                  placeholder="Select country"
                  value={selectCountry}
                  onChange={(option) => {
                    setSelectCountry(option);
                    setFieldValue("country_id", option.value);
                  }}
                  isDisabled={edittype == 1 || profile?.country_id}
                />
                <ErrorMessage
                  name="country_id"
                  component="div"
                  className="error"
                />
              </div>

              {/* Image Upload & Crop */}
              <div className="form-group position-relative">
                {validating && <UploadLoader />}
                <label className="label-s">Profile Image</label>
                <div
                  {...getRootProps({
                    className: "dropzone borderstylex",
                    style: { padding: 20, textAlign: "center" },
                  })}
                >
                  <input {...getInputProps()} />
                  <p>
                    Drag and drop an image or <span>click to upload</span>
                  </p>
                </div>

                {showCropper && imageUrl && (
                  <div
                    style={{
                      position: "relative",
                      width: 300,
                      height: 300,
                      margin: "20px auto",
                      background: "#333",
                    }}
                  >
                    <Cropper
                      image={imageUrl}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      showGrid={false}
                      onCropChange={setCrop}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                    />
                    <button
                      type="button"
                      onClick={showCroppedImage}
                      style={{
                        position: "absolute",
                        bottom: 10,
                        left: "50%",
                        transform: "translateX(-50%)",
                        padding: "5px 15px",
                        background: "#007bff",
                        color: "#fff",
                        border: "none",
                        borderRadius: 5,
                        cursor: "pointer",
                      }}
                    >
                      Crop & Save
                    </button>
                  </div>
                )}

                {imageUrl && !showCropper && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 10,
                    }}
                  >
                    <ProfileImageWithFallback url={imageUrl} className="" style={{
                      width: 120,
                      height: 120,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #ccc",
                    }} />
                  </div>
                )}
              </div>
            </ModalBody>
            <div className="modal-footer mt-3">
              <button className="btn" type="submit">
                Save
              </button>
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
}
